import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import InlineAlert from '../../../../shared/components/InlineAlert';

interface DatasetConfirmDialogProps {
  open: boolean;
  title: string;
  datasetName: string;
  confirmText: string;
  /** When set, the confirm stays disabled until the operator types the dataset name. */
  requireTypedName: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  children: React.ReactNode;
}

const DatasetConfirmDialog: React.FC<DatasetConfirmDialogProps> = ({
  open,
  title,
  datasetName,
  confirmText,
  requireTypedName,
  busy,
  error,
  onCancel,
  onConfirm,
  children,
}) => {
  const [typed, setTyped] = useState('');
  const nameMatches = !requireTypedName || typed === datasetName;

  // However the dialog closes, cancel or a confirm that succeeded, it opens again with no typed name.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
        {requireTypedName && (
          <>
            <Typography variant="body2">
              Type <strong>{datasetName}</strong> to confirm.
            </Typography>
            <TextField
              size="small"
              label="Dataset name"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
          </>
        )}
        {error && <InlineAlert severity="error">{error}</InlineAlert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={!nameMatches || busy}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DatasetConfirmDialog;
